import type { SchemaField } from '@cli-types/core-engine/schema.js'
import type { PathPart } from '@cli-types/next-adapter/forms.js'
import { formItemProps } from '@next-adapter/generators/form/form-shared/form-item-props.js'
import { formLabel } from '@next-adapter/generators/form/form-shared/form-label.js'
import { labelWithDescription } from '@next-adapter/generators/form/form-shared/label-with-description.js'
import { renderNestedFieldRenderer } from '@next-adapter/templates/renderers/generators/form/field-jsx-nested/render-nested-field.js'
import { pathExpression } from './path-expression.js'
import { pathNameProp } from './path-name-prop.js'
import { renderNestedObjectListField } from './render-nested-object-list-field.js'
import { renderTextInputField } from './render-text-input-field.js'

export function renderNestedField(
  field: SchemaField,
  indent: string,
  path: PathPart[],
  depth: number
): string {
  const nestedLabel = field.label || field.name
  const nestedHint = field.hint ? `<FormDescription>${field.hint}</FormDescription>` : ''

  if ((field.type === 'group' || field.type === 'section') && field.fields?.length) {
    const groupFields = field.fields
      .map((child) => renderNestedField(child, `${indent}    `, [...path, child.name], depth))
      .filter(Boolean)
      .join('\n')
    if (!groupFields) return ''

    const heading =
      nestedLabel && nestedLabel !== field.name
        ? `${indent}<h3 className="text-base font-medium">${nestedLabel}</h3>\n`
        : ''
    const columns = field.columns || 1
    return renderNestedFieldRenderer({
      kind: 'renderNestedFieldLayoutTemplate',
      props: {
        heading: heading,
        indent: indent,
        columnsGrid: columns > 1 ? `grid-cols-${columns}` : 'grid-cols-1',
        groupFields: groupFields
      }
    })
  }

  if (field.type === 'list' && field.fields?.length) {
    return renderNestedObjectListField(field, indent, nestedLabel, path, depth)
  }

  if (field.type === 'list') {
    const hideLabelProp = field.label ? '' : `\n${indent}  hideLabel`
    const descriptionProp = field.hint
      ? `\n${indent}  description={${JSON.stringify(field.hint)}}`
      : ''
    return renderNestedFieldRenderer({
      kind: 'renderNestedFieldDynamicListFieldTemplate',
      props: {
        indent: indent,
        pathExpression: pathExpression(path),
        nestedLabel: nestedLabel,
        hideLabelProp: hideLabelProp,
        descriptionProp: descriptionProp,
        fieldMaxItemsContent: field.maxItems ? `\n${indent}  maxItems={${field.maxItems}}` : ''
      }
    })
  }

  if (field.type === 'boolean') {
    return renderNestedFieldRenderer({
      kind: 'renderNestedFieldFormFieldTemplate',
      props: {
        indent: indent,
        pathNameProp: pathNameProp(path),
        formItemProps: formItemProps(field, 'flex flex-row items-start space-x-3 space-y-0'),
        labelWithDescription: labelWithDescription(
          `<FormLabel>${nestedLabel}</FormLabel>`,
          nestedHint,
          `${indent}      `
        )
      }
    })
  }

  if (field.type === 'image' || field.type === 'video' || field.type === 'media') {
    const acceptProp =
      field.type === 'image'
        ? ' accept="image/*"'
        : field.type === 'video'
          ? ' accept="video/*"'
          : ''
    return renderNestedFieldRenderer({
      kind: 'renderNestedFieldFormFieldDetailsTemplate',
      props: {
        indent: indent,
        pathNameProp: pathNameProp(path),
        formItemProps: formItemProps(field),
        labelWithDescription: labelWithDescription(
          formLabel(field, nestedLabel),
          nestedHint,
          `${indent}      `
        ),
        acceptProp: acceptProp
      }
    })
  }

  if (field.type === 'icon') {
    return renderNestedFieldRenderer({
      kind: 'renderNestedFieldFormFieldDetailsBranchTemplate',
      props: {
        indent: indent,
        pathNameProp: pathNameProp(path),
        formItemProps: formItemProps(field),
        labelWithDescription: labelWithDescription(
          formLabel(field, nestedLabel),
          nestedHint,
          `${indent}      `
        ),
        toLowerCase: nestedLabel.toLowerCase()
      }
    })
  }

  if (field.type === 'number' || field.type === 'decimal') {
    const stepProp = field.type === 'decimal' ? ' step="any"' : ''
    const emptyValue = field.required ? 'undefined' : 'null'
    return renderNestedFieldRenderer({
      kind: 'renderNestedFieldFormFieldDetailsBranchBodyTemplate',
      props: {
        indent: indent,
        pathNameProp: pathNameProp(path),
        formItemProps: formItemProps(field),
        labelWithDescription: labelWithDescription(
          formLabel(field, nestedLabel),
          nestedHint,
          `${indent}      `
        ),
        stepProp: stepProp,
        toLowerCase: nestedLabel.toLowerCase(),
        emptyValue: emptyValue
      }
    })
  }

  return renderTextInputField(field, indent, nestedLabel, nestedHint, path)
}
